import type { SourceSpec } from './entities';
import { isGateSatisfied } from './gate-condition';
import type { GateCondition } from './gate-condition';
import type { GridSize, SimState } from './state';
import { makeSource } from '../content/sources';

/**
 * A parcel of new ground, plus whatever sources come with it, that appears once its gate opens. The
 * map starts deliberately small — one source, one route — so the setup arc has nothing to get lost
 * in, and grows as the player earns their way into content that needs the extra room.
 */
export interface WorldExpansion {
  id: string;
  gate: GateCondition;
  grid: GridSize;
  sources: readonly SourceSpec[];
}

/**
 * Lands every expansion whose gate is open and that hasn't landed yet, returning their ids so the
 * caller can announce them. Idempotent via `state.appliedExpansions`, so a reload — or an undo past
 * the moment one landed — never stacks duplicate sources onto the map.
 */
export function applyExpansions(state: SimState, expansions: readonly WorldExpansion[]): string[] {
  const applied: string[] = [];
  for (const expansion of expansions) {
    if (state.appliedExpansions.includes(expansion.id)) continue;
    if (!isGateSatisfied(state, expansion.gate)) continue;

    state.grid = {
      width: Math.max(state.grid.width, expansion.grid.width),
      height: Math.max(state.grid.height, expansion.grid.height),
    };
    for (const source of expansion.sources) {
      state.sources.push(makeSource(source.position, source.yields, source.capacity));
    }
    state.appliedExpansions.push(expansion.id);
    applied.push(expansion.id);
  }
  return applied;
}
